import { REPORT_STATUS_LABELS } from '@safetyhub/shared';
import {
  reportPostSubmitFlow,
  type PostSubmitFlowSource,
} from '../pages/reports/postSubmitFlows';

/**
 * 보고서 상태 라벨 파생 — **데이터 축**으로 판정하는 단일 자리.
 *
 * `REPORT_STATUS_LABELS.APPROVED`는 '결재완료'다. 그런데 결재 정책이 없는 양식
 * (템플릿 `approvalLineMeta.defaultApprovalLine = []` → 서버
 * `report-composer.service.ts`의 `hasApprovalLinePolicy = templateLines.length > 0`가 false)은
 * 제출 즉시 `status=APPROVED`가 되고 `approval_signatures` 행이 **하나도 생기지 않는다**.
 * 결재를 한 번도 거치지 않은 문서에 '결재완료'라 적는 것은 거짓 안내다.
 *
 * ⚠️ `components/reports/manifest/approvalCompletion.ts`의 완료 모드
 * (`computeCompletionSubmission` / `isCompletionActionContext`)와 **다른 축**이다. 그쪽은
 * *제출을 누르면 결재 없이 끝나는가*를 **제출 전에 예측**하는 축이라, 비동기로 받아 온
 * 결재라인 칸(`serverHasApprovalLinePolicy` / 로드된 `approvalLines`)을 본다. 여기는
 * *결재를 실제로 거쳤는가*를 **완료된 뒤에** 판정하는 축이라 영속된 서명 행을 본다.
 * 결재 정책이 없는 양식(戊)에서는 두 축이 같은 답을 내지만(서버
 * `hasApprovalLinePolicy = templateLines.length > 0`), 이 축은 로딩 상태(`null`)가 없고
 * 목록 행에서도 쓸 수 있어 라벨에는 이쪽이 맞다.
 *
 * ⚠️ 이 모듈은 `lib/reports`를 **런타임 import하지 않는다.** 순수 술어를
 * `vi.mock('.../lib/reports')` 대상 모듈에 두면, 컴포넌트가 새 export를 import하는 순간
 * 그 모듈을 mock하는 **관련 없는 기존 spec 전부**가
 * `No "X" export is defined on the mock`으로 죽는다.
 *
 * ⚠️ `pages/reports/postSubmitFlows`는 런타임 import한다 — 위 규약을 깨지 않는다. 그 모듈의
 * 런타임 의존 subtree(`lib/tbmEducationProgress`·`pages/reports/siblingWriteHref`)는
 * `lib/reports`를 **`import type`으로만** 쓰기 때문이다(그래서 mock 표면과 무관하고, `lib/reports`가
 * 이 모듈을 런타임 import함에도 순환이 생기지 않는다). 이 성질이 깨지면 위 사고가 되살아나므로,
 * 그 subtree에 `lib/reports` 값 import를 더할 때는 여기 간선을 함께 검토할 것.
 */

/** 결재 정책 없이 즉시 확정된 완료본의 라벨. 구 TBM이 APPROVED를 부르던 낱말('완료')을 그대로 쓴다. */
export const POLICYLESS_COMPLETION_LABEL = '완료';

/**
 * 이어질 흐름의 설정이 끝나 **그 흐름이 돌고 있는** 문서의 라벨.
 *
 * 새로 지은 낱말이 아니다 — 구 `SIGNING`의 표시 문구가 '진행 중'이었고 교육 화면 배지도
 * 그렇게 부른다. 그 상태는 활동 축의 사실이라 문서 enum에서 걷혔지만(2026-09-08
 * `tbm-status-model-two-axes`), 사용자가 보던 말은 그대로 둔다.
 */
export const POST_SUBMIT_IN_PROGRESS_LABEL = '진행 중';

/**
 * 정본 맵(`REPORT_STATUS_LABELS`)에 없는 상태의 중립 라벨(2026-08-30 리뷰 INFO — 종전엔 raw enum을
 * 그대로 돌려줘 화면에 코드값이 샐 수 있었다, `display-fallback-leaks-raw-enum` 계열). 실제로 닿는
 * 상태(Prisma enum 6종)는 전부 맵에 있으므로 정상 데이터에서는 나오지 않는다 — 서버가 새 상태를
 * 먼저 내려보내는 배포 순서 어긋남·손상 데이터의 방어선이다. 정보 손실 없음: 폐기된 구 상태
 * (`SIGNING`·`REVIEWING`)는 2026-09-08 마이그레이션으로 데이터에서도 걷혀 raw로 구분할 것이 없다.
 */
export const UNKNOWN_REPORT_STATUS_LABEL = '알 수 없음';

// 유형별 상태 어휘 오버라이드 맵을 두지 않는다 — 교육이 도는 COMPOSED TBM의 어휘는 enum 키가
// 아니라 **데이터 판정**이 정한다(아래 `isPostSubmitFlowInProgress`('진행 중')·
// `isApprovedWithoutApproval`('완료')). 되살리면 같은 상태를 두 자리에서 다른 말로 부르게 된다.

/**
 * 판정에 필요한 최소 형상 — `Report`(상세·목록 응답)가 이 subset을 만족한다.
 *
 * 흐름 판정 재료(`PostSubmitFlowSource`)를 **확장**한다. 재료 목록을 여기 손으로 적지 않는
 * 이유: 그러면 특정 흐름의 어휘가 이 모듈로 새고, 다른 흐름이 등록되는 순간 낡는다. 확장으로
 * 두면 레지스트리가 재료를 늘릴 때 이 형상이 따라 넓어진다.
 */
export interface ReportStatusLabelSource extends PostSubmitFlowSource {
  status: string;
  /**
   * 결재 서명 행. 상세(`GET /reports/:id`)·목록(`GET /reports`) 응답 **둘 다** 싣는다
   * (목록은 `reports.service.ts` findAll include → `...report` spread).
   */
  approvalSignatures?: unknown[] | null;
}

/**
 * "APPROVED인데 결재를 한 번도 거치지 않았다"인가.
 *
 * 판정 축은 **서명 행 0개**다. 제출(`report-approval.service.ts`)은 결재라인의 **모든** 칸에
 * 행을 만든다 — 전자 칸은 `PENDING`, 담당자 미지정(오프라인 수기) 칸과 작성자 preset 칸은
 * `APPROVED`(무서명), OFFLINE 모드는 전원 `APPROVED`. 즉 서명 행 0개는
 * "만들 칸 자체가 없었다" = 결재 정책 없음과 동치다.
 *
 * ⚠️ `undefined`(응답이 필드를 안 실었다)와 `[]`(실었는데 0행)는 **다르게** 다룬다.
 * `undefined`를 0행으로 보면, 필드를 안 싣는 surface에서 **진짜 결재완료본까지** '완료'로
 * 낮춰 부르게 된다(정보 손실). 모르면 정본 라벨을 유지하는 쪽이 안전하다.
 */
export function isApprovedWithoutApproval(report: ReportStatusLabelSource): boolean {
  return (
    report.status === 'APPROVED' &&
    Array.isArray(report.approvalSignatures) &&
    report.approvalSignatures.length === 0
  );
}

/**
 * "제출은 끝났고 이어질 흐름이 돌고 있다"인가 — 즉 「초안」이라 부르면 거짓인 `DRAFT`인가.
 *
 * 판정 축은 **「양식이 그 흐름을 선언했는가 + 그 흐름의 설정이 끝났는가」** 둘이고, 둘 다
 * `pages/reports/postSubmitFlows` 레지스트리가 소유한다. 여기서 `templateKey`로 유추하거나
 * 설정 축 필드를 직접 비교하지 **않는** 이유가 그것이다 — 그러면 특정 흐름의 어휘가 이 모듈로
 * 새고, 다른 흐름이 등록되는 순간 거짓이 된다(같은 축을 두 곳에 적으면 두 곳이 갈린다).
 *
 * `DRAFT`로 좁히는 것이 계약이다. 그 앞(CREATING)은 아직 만드는 중이라 설정이 끝날 수 없고,
 * 그 뒤(PENDING_APPROVAL·APPROVED·REJECTED)는 결재 축이 이미 라벨을 소유한다 — 이 판정은
 * **결재 전 구간**만 다룬다.
 *
 * 2026-09-08 결정(`tbm-status-model-two-axes` §3): 교육이 도는 TBM은 `SIGNING`이 enum에서
 * 걷힌 뒤 `DRAFT`로 산다. 그 문서를 화면이 「초안」이라 부르는 것이 이 판정이 닫는 구간이다.
 * 재료(스냅샷·설정 축)를 안 실은 surface는 undefined → 미선언·미완으로 읽혀 종전 라벨 그대로다.
 */
export function isPostSubmitFlowInProgress(report: ReportStatusLabelSource): boolean {
  if (report.status !== 'DRAFT') return false;
  const entry = reportPostSubmitFlow(report);
  return !!entry && entry.isSetupComplete(report);
}

/**
 * 보고서 데이터 기준 상태 라벨.
 *
 * 상태(enum)만으로는 표현할 수 없는 축이 둘 있어서 report 객체를 받는다 — 위
 * `isApprovedWithoutApproval`(결재를 거쳤는가)와 `isPostSubmitFlowInProgress`(이어질 흐름이
 * 도는가). 그 외에는 `REPORT_STATUS_LABELS` 그대로다 — 양식(문서 종류)은 라벨을 바꾸지 않는다.
 *
 * 두 데이터 판정은 status로 배타(APPROVED / DRAFT)라 순서가 결과를 바꾸지 않는다.
 *
 * 상태 enum만 아는 자리(결재함 행 등)도 이 함수를 `{ status }`로 부른다 — 구 `getDocStatusLabel`·
 * `getReportStatusLabel`(enum→라벨, `|| status` 폴백)은 2026-08-30 리뷰 INFO로 접었다. 서명 행을
 * 안 실은 입력은 `isApprovedWithoutApproval`이 `undefined`를 "모른다"로 읽어 정본 라벨을 유지한다.
 */
export function resolveReportStatusLabel(report: ReportStatusLabelSource): string {
  if (isApprovedWithoutApproval(report)) return POLICYLESS_COMPLETION_LABEL;
  if (isPostSubmitFlowInProgress(report)) return POST_SUBMIT_IN_PROGRESS_LABEL;

  const canonical = REPORT_STATUS_LABELS as Record<string, string>;
  // 모르는 상태는 코드값을 화면에 흘리지 않는다(위 상수 JSDoc).
  return canonical[report.status] ?? UNKNOWN_REPORT_STATUS_LABEL;
}
